/**
 * Flying Bookmark Animation
 * Animates a bookmark icon from the bookmark button to the profile image in the header
 */

import {EASING_CURVES} from './motion/easing.js';

/**
 * Animate a bookmark icon flying from the source button to the profile image
 * @param {HTMLElement} bookmarkButton - The bookmark button element that was clicked
 */
export function triggerFlyingBookmarkAnimation(bookmarkButton) {
    // Find the bookmark icon inside the button
    const sourceIcon = bookmarkButton.querySelector('.svg-icon');
    if (!sourceIcon) {
        console.warn('Bookmark icon not found inside button, skipping animation');
        return;
    }

    const headerProfileImage = document.querySelector('.js-header-profile-image');
    if (!headerProfileImage) {
        console.warn('Header profile image not found (.js-header-profile-image), skipping animation');
        return;
    }

    const startRect = sourceIcon.getBoundingClientRect();
    const endRect = headerProfileImage.getBoundingClientRect();

    // Calculate target center position, clamping to viewport edges so the icon
    // flies to the screen edge instead of to an invisible off-screen position
    const targetCenterX = Math.max(0, Math.min(endRect.left + endRect.width / 2 - startRect.width / 2, window.innerWidth));
    const targetCenterY = Math.max(0, Math.min(endRect.top + endRect.height / 2 - startRect.height / 2, window.innerHeight));

    // Scale duration by flight distance so long-distance animations (e.g. video player → header) feel smooth
    const deltaX = targetCenterX - startRect.left;
    const deltaY = targetCenterY - startRect.top;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const duration = Math.min(Math.max(distance / 1000, 0.6), 1.2);
    const durationMs = duration * 1000;

    const horizontalWrapper = document.createElement('div');
    horizontalWrapper.style.cssText = `
        position: fixed;
        z-index: 9999;
        pointer-events: none;
        left: ${startRect.left}px;
        top: 0;
        width: ${startRect.width}px;
        height: ${startRect.height}px;
        transition: none;
    `;

    // Create a styled container for the icon (white bg, rounded corners, shadow)
    const iconContainer = document.createElement('span');
    iconContainer.style.cssText = `
        position: absolute;
        left: 0;
        top: ${startRect.top}px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 8px;
        background-color: white;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        transition: none;
    `;

    /** @type {HTMLElement} */
    const flyingIcon = /** @type {HTMLElement} */ (sourceIcon.cloneNode(true));
    flyingIcon.style.cssText = `
        width: ${startRect.width}px;
        height: ${startRect.height}px;
    `;

    flyingIcon.classList.add('fg-graphic-brand-default');

    iconContainer.append(flyingIcon);
    horizontalWrapper.append(iconContainer);
    document.body.append(horizontalWrapper);

    // Start curved flying animation after a delay so user can see the icon
    // Different easing for X and Y creates a curved arc path
    setTimeout(() => {
        // Horizontal movement: ease-in (starts slow, ends fast)
        horizontalWrapper.style.transition = `left ${duration}s ${EASING_CURVES['ease-in']}`;
        horizontalWrapper.style.left = `${targetCenterX}px`;

        // Vertical movement: ease-out (starts fast, ends slow) creates an arc
        iconContainer.style.transition = `top ${duration}s ${EASING_CURVES['ease-out']}`;
        iconContainer.style.top = `${targetCenterY}px`;
    }, 150);

    // Scale down at the end of the flight (instead of opacity fade)
    setTimeout(() => {
        iconContainer.style.transition = `top ${duration}s ${EASING_CURVES['ease-out']}, transform 0.2s ease-out`;
        iconContainer.style.transform = 'scale(0)';
    }, 150 + durationMs - 100);

    setTimeout(() => {
        horizontalWrapper.remove();
    }, 150 + durationMs + 150);
}
